/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { Drawer, IModuleProps, INodeProps, Module } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { validateCatalogId } from '@msdyn365-commerce-modules/retail-actions';
import { AttributesForSelectedVariantInput, getAttributesForSelectedVariant } from '@msdyn365-commerce-modules/product-specification';
import { ProductSpecificationTableRow } from './components/dyn-product-specification-table-stucture';
import { IDynProductSpecificationData } from './dyn-product-specification.data';
import { displayStyle, IDynProductSpecificationProps } from './dyn-product-specification.props.autogenerated';

import { getParameter } from '../../common/utilities/global-parameters.utils';
//testGlobalParametersHelpers

/**
 * Maintain Drawer State.
 */
export interface IProductSpecificationState {
    isDrawerOpen: boolean;
    isAdditionalMediaDrawerOpen: boolean;
    drawerListId: number | undefined;
}

/**
 * Product specification view props.
 */
export interface IProductSpecificationViewProps extends IDynProductSpecificationProps<{}> {
    ProductSpecification: IModuleProps;
    ProductSpecificationTableProps: INodeProps;
    ProductSpecificationTableBodyProps: INodeProps;
    productSpecificationResult?: (JSX.Element | null)[];

    title?: React.ReactNode;
    isTabs?: boolean;
    isAccrodion?: boolean;
    tabsData?: ITabsData;
    handleText?(event: Msdyn365.ContentEditableEvent): void;
}

/**
 * Check for display style either tabs or accordion or table.
 */
export interface IProductSpecificationExtentedProps extends IDynProductSpecificationProps<IDynProductSpecificationData> {
    displayStyle?: displayStyle;
}

/**
 * Tabs view interfaces
 */
// AJOUTER ces interfaces au début du fichier après les imports

export interface ITabsData {
    tabs: ITabData[];
}

export interface ITabData {
    id: string;
    label: string;
    items: ITabItem[];
    order: number;
    isProductDetailsTab?: boolean;
    isIngredientsTab?: boolean;
    isInstructionsTab?: boolean;
    isNutritionalTab?: boolean;
    isOthersTab?: boolean;
}

export interface ITabItem {
    name: string;
    cellData: JSX.Element;
    technicalName?: string;
    value?: any;
    unitOfMeasureSymbol?: string;
}

export interface IGroupedSpecification {
    categoryId: string;
    categoryName: string;
    specifications: AttributeValue[];
    order: number;
}

export interface ICategoryConfig {
    id: string;
    name: string;
    order: number; // Ordre d'affichage des onglets
    matcher: (attributeName: string, attributeId: number) => boolean;
    specificationOrder?: string[]; // Ordre des technicalName qui sera respecté dans cette catégorie
    // Propriétés spécifiques à la catégorie par défaut "others"
    excludeSubstrings?: string[]; // Sous-chaînes à exclure dans attributeName
    includeWithoutTechnicalName?: boolean; // Inclure les attributs sans technicalName
}

/**
 * Tabs Helpers
 * @param tabsCount
 * @returns
 */

export const useAccessibleTabs = (tabsCount: number) => {
    const [activeTab, setActiveTab] = React.useState(0);

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent) => {
            switch (event.key) {
                case 'ArrowLeft':
                    event.preventDefault();
                    setActiveTab(prev => (prev === 0 ? tabsCount - 1 : prev - 1));
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    setActiveTab(prev => (prev === tabsCount - 1 ? 0 : prev + 1));
                    break;
                case 'Home':
                    event.preventDefault();
                    setActiveTab(0);
                    break;
                case 'End':
                    event.preventDefault();
                    setActiveTab(tabsCount - 1);
                    break;
            }
        },
        [tabsCount, setActiveTab]
    );

    return { activeTab, setActiveTab, handleKeyDown };
};

/********************************
 *
 * Tab Categories :
 *  - tabProductDetails
 *  - tabIngredients
 *  - tabInstructions
 *  - tabNutrition
 *  - tabAllergens
 *  - tabOthers
 *
 ********************************/
const getCategoryConfigs = (props: IProductSpecificationExtentedProps): ICategoryConfig[] => {
    return [
        {
            id: 'tabProductDetails',
            name: props.config.tabLabelProductDetails ?? 'Product details',
            order: 1,
            specificationOrder: ['WBS_SN', 'WBS_BRAND'], // Ordre souhaité au sein du groupe
            matcher: (attributeName: string, attributeId: number) => {
                const matchingAttributes = ['WBS_SN', 'WBS_BRAND'];
                const technicalName = getParameter(attributeId.toString(), props.context.app as Msdyn365.IAppSettings);
                // console.log('🔍 Matcher - technicalName:', technicalName, 'attributeId:', attributeId);
                return matchingAttributes.some(attr => technicalName?.toUpperCase().includes(attr.toUpperCase()));
            }
        },
        {
            id: 'tabIngredients',
            name: props.config.tabLabelIngredients ?? 'Ingredients',
            order: 2,
            specificationOrder: ['WBS_ITEM_INGREDIENTS'], // Ordre souhaité au sein du groupe
            matcher: (attributeName: string, attributeId: number) => {
                const matchingAttributes = ['WBS_ITEM_INGREDIENTS'];
                const technicalName = getParameter(attributeId.toString(), props.context.app as Msdyn365.IAppSettings);
                return matchingAttributes.some(attr => attr.toUpperCase() === technicalName?.toUpperCase());
            }
        },
        {
            id: 'tabInstructions',
            name: props.config.tabLabelInstructions ?? 'Instructions',
            order: 3,
            specificationOrder: ['WBS_USER_INSTRUCTION', 'WBS_INSTRUCTIONS_FOR_STORAGE'], // Ordre souhaité au sein du groupe
            matcher: (attributeName: string, attributeId: number) => {
                const matchingAttributes = ['WBS_USER_INSTRUCTION', 'WBS_INSTRUCTIONS_FOR_STORAGE'];
                const technicalName = getParameter(attributeId.toString(), props.context.app as Msdyn365.IAppSettings);
                return matchingAttributes.some(attr => attr.toUpperCase() === technicalName?.toUpperCase());
            }
        },
        {
            id: 'tabNutrition',
            name: props.config.tabLabelNutrition ?? 'Nutritional values',
            order: 4,
            specificationOrder: ['wbs_nv'], // Ordre souhaité au sein du groupe
            matcher: (attributeName: string, attributeId: number) => {
                const matchingAttributes = ['WBS_NV_'];
                const technicalName = getParameter(attributeId.toString(), props.context.app as Msdyn365.IAppSettings);
                return matchingAttributes.some(attr => technicalName?.toUpperCase().includes(attr.toUpperCase()));
            }
        },
        {
            id: 'tabOthers',
            name: props.config.tabLabelOthers ?? 'Others',
            order: 999,
            // Configuration des exclusions
            excludeSubstrings: [
                'WBS_ITEM_NAME',
                'WBS_ITEM_DESCRIPTION',
                'WBS_ITEM_CATEGORY',
                'WBS_SALES_UNIT',
                'WBS_LABELLING_SUGGESTION',
                'WBS_VOLUME_PRICING',
                'WBS_NEW_ITEM',
                'WBS_LABELLING_SUGGESTION',
                'WBS_MTO_S002',
                'WBS REJO_TDS',
                'WBS_CULINA_PREP_',
                'WBS_REJO_TDS_URL',
                'Storing_conditions_Factory',
                'WBS_CUST_HIERARCHY_SPECIFIC',
                'WBS_SALES_REPRESENTATIVE',
                'WBS_VOLUME_PRICING_TABLE'
            ],
            includeWithoutTechnicalName: false, // true = inclure, false = exclure les attributs sans technicalName
            matcher: (attributeName: string, attributeId: number) => {
                return true; // La logique d'exclusion est gérée dans _groupProductSpecifications
            }
        }
    ];
};

/**
 *
 * ProductSpecification component.
 * @extends {React.Component<IDynProductSpecificationProps<IDynProductSpecificationData>>}
 */
@observer
class ProductSpecification extends React.Component<IProductSpecificationExtentedProps, IProductSpecificationState> {
    public constructor(props: IProductSpecificationExtentedProps) {
        super(props);
        this.state = {
            isDrawerOpen: false,
            isAdditionalMediaDrawerOpen: false,
            drawerListId: undefined
        };

        // Test de toutes les fonctions helpers
        // testGlobalParametersHelpers(this.props.context.app as Msdyn365.IAppSettings);
    }

    public async componentDidMount(): Promise<void> {
        const { context, data, telemetry } = this.props;
        const catalogId = Msdyn365.getCatalogId(this.props.context.request);
        validateCatalogId(catalogId);
        reaction(
            () => (data.product.result ? data.product.result.RecordId : null),
            () => {
                if (context && data.product.result) {
                    const actionInput = new AttributesForSelectedVariantInput(
                        data.product.result.RecordId,

                        +context.actionContext.requestContext.apiSettings.channelId,
                        data.product.result,
                        catalogId
                    );

                    getAttributesForSelectedVariant(actionInput, this.props.context.actionContext).catch((error: Error) => {
                        telemetry.error(error.message);
                        telemetry.debug('Unable to update attributes for Product');
                        telemetry.error(error.message);
                    });
                }
            }
        );
    }

    public shouldComponentUpdate(nextProps: IProductSpecificationExtentedProps, nextState: IProductSpecificationState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        let data: IDynProductSpecificationData;
        try {
            data = this.props.data;
        } catch (error) {
            this.props.telemetry.error(`Something went wrong ------${error}`);
            return <div>{error}</div>;
        }

        if (!data.productSpecificationData.result || data.productSpecificationData.result.length <= 0) {
            this.props.telemetry.error('Product specification content is empty, module wont render.');
            return null;
        }

        const { heading, className } = this.props.config;
        const { productSpecificationData } = this.props.data;
        const isTabs = this.props.config.displayStyle === displayStyle.tabs;
        const isAccrodion = this.props.config.displayStyle === displayStyle.accordion;
        const viewProps = {
            ...this.props,
            handleText: this.handleHeadingChange,
            ProductSpecification: {
                moduleProps: this.props,
                className: classnames('ms-product-specification', className)
            },
            ProductSpecificationTableProps: isAccrodion
                ? {
                      moduleProps: this.props,
                      className: classnames('ms-product-specification__accordion', className)
                  }
                : {
                      className: classnames('ms-product-specification__table table-striped table-bordered'),
                      tag: 'table'
                  },
            isAccrodion,
            ProductSpecificationTableBodyProps: isAccrodion
                ? {}
                : {
                      className: '',
                      tag: 'tbody'
                  },
            title: heading?.text && (
                <Msdyn365.Text
                    className='ms-product-specification__heading'
                    tag={heading.tag ?? 'h2'}
                    text={heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            isTabs,
            tabsData: isTabs ? this._createTabsData(data) : undefined,
            productSpecificationResult:
                //productSpecificationData.result && (isAccrodion ? this._createDrawerBody(data) : this._createTableBody(data))
                productSpecificationData.result && !isTabs && (isAccrodion ? this._createDrawerBody(data) : this._createTableBody(data))
        } as IProductSpecificationViewProps;

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle Heading Text Change.
     * @param event - Current event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    private _createTableBody(content: IDynProductSpecificationData): (JSX.Element | null)[] {
        const tableRows = content.productSpecificationData.result!.map((product, index) => {
            const cellData: JSX.Element | null = this._renderProductCell(product);

            if (!cellData) {
                return null;
            }

            return (
                <>
                    <ProductSpecificationTableRow
                        className='ms-product-specification__table-row'
                        key={index}
                        productName={product.Name}
                        cellData={cellData}
                    />
                </>
            );
        });

        if (this.props.context.request.app?.config?.OmniChannelMedia && this.props.data.additionalMediaLocations.result?.length) {
            tableRows.push(this._createAdditionalMediaDrawer());
        }

        return tableRows;
    }

    private _createDrawerBody(content: IDynProductSpecificationData): (JSX.Element | null)[] {
        const accordionItemContainer: IModuleProps = {
            moduleProps: this.props,
            className: 'ms-product-specification-title'
        };

        const drawers = content.productSpecificationData.result!.map(product => {
            const cellData: JSX.Element | null = this._renderProductCell(product);
            if (!cellData) {
                return null;
            }
            return (
                <Module {...accordionItemContainer} key={product.RecordId}>
                    <Drawer
                        key={product.RecordId}
                        collapseProps={{
                            timeout: 0,
                            isOpen: this.state.isDrawerOpen && this.state.drawerListId === product.RecordId
                        }}
                        className='ms-product-specification__drawer'
                        openGlyph='ms-product-specification__drawer-open'
                        closeGlyph='ms-product-specification__drawer-close'
                        glyphPlacement='end'
                        toggleButtonText={product.Name ?? ''}
                        onToggle={this._toggle(product.RecordId)}
                    >
                        <div className='ms-product-details'>{cellData}</div>
                    </Drawer>
                </Module>
            );
        });

        if (this.props.context.request.app?.config?.OmniChannelMedia && this.props.data.additionalMediaLocations.result?.length) {
            drawers.push(this._createAdditionalMediaDrawer());
        }

        return drawers;
    }
    private _createAdditionalMediaDrawer(): JSX.Element {
        const accordionItemContainer: IModuleProps = {
            moduleProps: this.props,
            className: 'ms-product-specification-title'
        };

        return (
            <Module {...accordionItemContainer} key={'additional_media_drawer'}>
                <Drawer
                    key={'additional_media_drawer'}
                    collapseProps={{
                        timeout: 0,
                        isOpen: this.state.isAdditionalMediaDrawerOpen
                    }}
                    className='ms-product-specification__drawer'
                    openGlyph='ms-product-specification__drawer-open'
                    closeGlyph='ms-product-specification__drawer-close'
                    glyphPlacement='end'
                    toggleButtonText={this.props.resources.additionalDownloadsText}
                    onToggle={() => {
                        this.setState(prevState => ({
                            ...prevState,
                            isAdditionalMediaDrawerOpen: !prevState.isAdditionalMediaDrawerOpen
                        }));
                    }}
                >
                    <div className='ms-product-details'>
                        <ul className='ms-product-details-additional-media'>
                            {(this.props.data.additionalMediaLocations.result || []).map(mediaLocation => (
                                <li key={mediaLocation.Uri}>
                                    <a href={mediaLocation.Uri} download>
                                        {mediaLocation.AltText}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Drawer>
            </Module>
        );
    }

    private readonly _toggle = (drawerListId: number | undefined) => (): void => {
        if (this.state.drawerListId !== drawerListId) {
            this.setState({ drawerListId, isDrawerOpen: true });
        } else {
            this.setState({ drawerListId: undefined, isDrawerOpen: false });
        }
    };

    private _renderProductCell(product: AttributeValue): JSX.Element | null {
        switch (product.DataTypeValue) {
            case 0: // None
                // Return span here instead of null so the row still shows
                return <span />;
            case 1: // Currency
                return this._renderCurrencyCell(product);
            case 2: // DateTime
                return this._renderDateTimeCell(product);
            case 3: // Decimal
                return this._renderDecimalCell(product);
            case 4: // Integer
                return this._renderIntegerCell(product);
            case 5: // Text
                return this._renderTextCell(product);
            case 6: // TrueFalse
                return this._renderTrueFalseCell(product);
            case 40: // Video
                this.props.telemetry.warning('Got video specification type, not supported yet so skipping line');
                return null;
            case 41: // Image
                this.props.telemetry.warning('Got image specification type, not supported yet so skipping line');
                return null;
            default:
                this.props.telemetry.warning(`Got unknown specification type ${product.DataTypeValue} so skipping line`);
                return null;
        }
    }

    private _getRawValue(product: AttributeValue): string | number | boolean | null | undefined {
        switch (product.DataTypeValue) {
            case 1: // Currency
                return product.CurrencyValue;
            case 2: // DateTime
                return product.DateTimeOffsetValue ? product.DateTimeOffsetValue.toUTCString() : null;
            case 3: // Decimal
                return product.FloatValue;
            case 4: // Integer
                return product.IntegerValue;
            case 5: // Text
                return product.TextValue;
            case 6: // TrueFalse
                return product.BooleanValue ? this.props.resources.trueValueText ?? 'Yes' : this.props.resources.falseValueText ?? 'No';
            default:
                return null;
        }
    }

    private _renderCurrencyCell(product: AttributeValue): JSX.Element | null {
        if (product.CurrencyValue !== null && product.CurrencyValue !== undefined) {
            return (
                <span>
                    {product.CurrencyCode}
                    {product.CurrencyValue}
                </span>
            );
        }

        return null;
    }

    private _renderDateTimeCell(product: AttributeValue): JSX.Element | null {
        if (product.DateTimeOffsetValue?.toUTCString) {
            return <span>{product.DateTimeOffsetValue.toUTCString()}</span>;
        }

        return null;
    }

    private _renderIntegerCell(product: AttributeValue): JSX.Element | null {
        if (product.IntegerValue !== null && product.IntegerValue !== undefined) {
            return <span>{product.IntegerValue}</span>;
        }

        return null;
    }

    private _renderDecimalCell(product: AttributeValue): JSX.Element | null {
        if (product.FloatValue !== null && product.FloatValue !== undefined) {
            return <span>{product.FloatValue}</span>;
        }

        return null;
    }

    private _renderTextCell(product: AttributeValue): JSX.Element | null {
        if (product.TextValue !== null && product.TextValue !== undefined) {
            // eslint-disable-next-line react/no-danger
            return <span dangerouslySetInnerHTML={Msdyn365.getRichTextHtml(product.TextValue)} />;
        }

        return null;
    }

    private _renderTrueFalseCell(product: AttributeValue): JSX.Element | null {
        // TODO 20369885 setting to default value is needed to work around a platform bug where
        // config string value does not get its default value.
        if (product.BooleanValue) {
            return <span>{this.props.resources.trueValueText ? this.props.resources.trueValueText : 'Yes'}</span>;
        }
        return <span>{this.props.resources.falseValueText ? this.props.resources.falseValueText : 'No'}</span>;
    }

    /**
     * Create ordered tabs data structure from product specifications
     */
    private _createTabsData(content: IDynProductSpecificationData): ITabsData {
        const groupedData = this._groupProductSpecifications(content.productSpecificationData.result!);

        const sortedGroups = groupedData.sort((a, b) => a.order - b.order);

        return {
            tabs: sortedGroups
                .map(group => ({
                    id: group.categoryId,
                    label: group.categoryName,
                    order: group.order,
                    isProductDetailsTab: group.categoryId === 'tabProductDetails',
                    isInstructionsTab: group.categoryId === 'tabInstructions',
                    isIngredientsTab: group.categoryId === 'tabIngredients',
                    isNutritionalTab: group.categoryId === 'tabNutrition',
                    isOthersTab: group.categoryId === 'tabOthers',
                    items: group.specifications
                        .map(spec => {
                            // Récupérer le technicalName
                            const specId = spec.RecordId ? spec.RecordId.toString() : '0';
                            const technicalName = getParameter(specId, this.props.context.app as Msdyn365.IAppSettings);
                            // console.log(`🔍 Technical Name for ${spec.Name}: ${technicalName} (ID: ${specId})`);
                            // console.log(`🔍 Unit of Measure for ${spec.Name}: ${spec.UnitOfMeasureSymbol}`);

                            return {
                                name: this._getDisplayName(spec, group.categoryId) || '',
                                cellData: this._renderProductCell(spec),
                                technicalName: technicalName || undefined,
                                value: this._getRawValue(spec),
                                unitOfMeasureSymbol: spec.UnitOfMeasureSymbol || undefined
                            };
                        })
                        .filter(item => item.cellData !== null) as ITabItem[]
                }))
                .filter(tab => tab.items.length > 0)
        };
    }

    /**
     * Ordonne les spécifications selon l'ordre défini dans la configuration de catégorie
     */
    private _sortSpecificationsByOrder(specifications: AttributeValue[], specificationOrder?: string[]): AttributeValue[] {
        if (!specificationOrder || specificationOrder.length === 0) {
            return specifications;
        }

        const appSettings = this.props.context.app as Msdyn365.IAppSettings;

        return specifications.sort((a, b) => {
            const aId = a.RecordId ? a.RecordId.toString() : '0';
            const bId = b.RecordId ? b.RecordId.toString() : '0';
            const aTechnicalName = getParameter(aId, appSettings);
            const bTechnicalName = getParameter(bId, appSettings);

            // La valeur de specificationOrder peut être une sous-chaîne du technicalName
            const aIndex = aTechnicalName
                ? specificationOrder.findIndex(name => aTechnicalName.toUpperCase().includes(name.toUpperCase()))
                : -1;
            const bIndex = bTechnicalName
                ? specificationOrder.findIndex(name => bTechnicalName.toUpperCase().includes(name.toUpperCase()))
                : -1;

            if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
            }
            if (aIndex !== -1 && bIndex === -1) {
                return -1;
            }
            if (aIndex === -1 && bIndex !== -1) {
                return 1;
            }
            return (a.Name || '').localeCompare(b.Name || '');
        });
    }

    /**
     * Vérifie si un attribut doit être exclu de la catégorie "others"
     */
    private _shouldExcludeFromOthers(attributeName: string, attributeId: number, config: ICategoryConfig): boolean {
        const appSettings = this.props.context.app as Msdyn365.IAppSettings;
        const technicalName = getParameter(attributeId.toString(), appSettings) || '';

        // 1. Vérification des sous-chaînes à exclure dans attributeName
        if (config.excludeSubstrings && config.excludeSubstrings.length > 0) {
            const hasExcludedSubstring = config.excludeSubstrings.some(substring =>
                technicalName.toLowerCase().includes(substring.toLowerCase())
            );

            if (hasExcludedSubstring) {
                // console.log(`🚫 Exclusion par substring: "${attributeName}/${technicalName}" contient une sous-chaîne exclue`);
                return true;
            }
        }

        // 2. Gestion des attributs sans technicalName
        if (!technicalName || technicalName.trim() === '') {
            const shouldInclude = config.includeWithoutTechnicalName ?? true;

            if (!shouldInclude) {
                // console.log(`🚫 Exclusion sans technicalName: "${attributeName}" (ID: ${attributeId})`);
                return true;
            } else {
                // console.log(`✅ Inclusion sans technicalName: "${attributeName}" (ID: ${attributeId})`);
            }
        }

        return false;
    }

    /**
     * Group product specifications by category using configurable rules (with ordered content within each group)
     */
    private _groupProductSpecifications(specifications: AttributeValue[]): IGroupedSpecification[] {
        const categoryConfigs = getCategoryConfigs(this.props);
        const groups = new Map<string, IGroupedSpecification>();

        categoryConfigs.forEach(config => {
            groups.set(config.id, {
                categoryId: config.id,
                categoryName: config.name,
                specifications: [],
                order: config.order
            });
        });

        specifications.forEach(spec => {
            const attributeName = spec.Name || '';
            const attributeId = spec.RecordId || 0;

            let categoryAssigned = false;

            // Tester les catégories spécifiques (pas "tabOthers")
            for (const config of categoryConfigs.filter(c => c.id !== 'tabOthers')) {
                if (config.matcher(attributeName, attributeId)) {
                    const group = groups.get(config.id);
                    if (group) {
                        group.specifications.push(spec);
                        categoryAssigned = true;
                        break;
                    }
                }
            }

            // Si pas assigné à une catégorie spécifique, tester "others" avec exclusions
            if (!categoryAssigned) {
                const othersConfig = categoryConfigs.find(c => c.id === 'tabOthers');
                const otherGroup = groups.get('tabOthers');

                if (othersConfig && otherGroup) {
                    // 🆕 Vérifier les exclusions pour "others"
                    const shouldExclude = this._shouldExcludeFromOthers(attributeName, attributeId, othersConfig);

                    if (!shouldExclude && othersConfig.matcher(attributeName, attributeId)) {
                        otherGroup.specifications.push(spec);
                        // console.log(`✅ Ajouté à tabOthers: "${attributeName}" (ID: ${attributeId})`);
                    } else if (shouldExclude) {
                        // console.log(`🚫 Exclu de tabOthers: "${attributeName}" (ID: ${attributeId})`);
                    }
                }
            }
        });

        // Trier les spécifications dans chaque groupe selon l'ordre défini
        const sortedGroups = Array.from(groups.values()).map(group => {
            const categoryConfig = categoryConfigs.find(config => config.id === group.categoryId);

            return {
                ...group,
                specifications: this._sortSpecificationsByOrder(group.specifications, categoryConfig?.specificationOrder)
            };
        });

        return sortedGroups.filter(group => group.specifications.length > 0);
    }

    /**
     * Determine the display name for a product specification based on category and spec data.
     * @param spec - Product specification data
     * @param categoryId - Category Id (see function getCategoryConfigs)
     * @returns Display name string
     */
    private _getDisplayName(spec: any, categoryId?: string): string {
        // Cas 1: Si c'est une catégorie spécifique, utiliser un format particulier
        // if (categoryId === 'tabNutrition') {
        //     return `Nutrition label TODO : ${spec.Name || ''}`;
        // }

        // Cas par défaut
        return spec.Name || '';
    }
}

export default ProductSpecification;
